import { Controller } from "@hotwired/stimulus"

// Adds a small GeoJSON circle layer (place dots) on top of a mapkick/Mapbox GL map.
// Periodically refreshes to stay in sync with the dashboard refresh cycle.
export default class extends Controller {
  static values = {
    url:   String,
    refresh: Number,   // milliseconds
    mapId: String,     // id attribute of the js_map element
    fixedView: String, // "australia" keeps the whole continent framed; "" = no override
    taskersUrl: String // active-tasker points endpoint; "" = layer disabled (e.g. /analytics/one)
  }

  // Mainland Australia + Tasmania bounding box [[west, south], [east, north]].
  static AUSTRALIA_BOUNDS = [[112.9, -43.7], [153.7, -10.4]]

  connect() {
    this.addLayer()
    if (this.taskersUrlValue) this.addTaskersLayer()
    this.applyFixedView()
    if (this.refreshValue > 0) {
      this.interval = setInterval(() => {
        this.refreshLayer()
        if (this.taskersUrlValue) this.refreshTaskersLayer()
      }, this.refreshValue)
    }
  }

  disconnect() {
    if (this.interval) clearInterval(this.interval)
    if (this.resizeHandler) window.removeEventListener("resize", this.resizeHandler)
  }

  // Frame the whole of Australia and keep it framed on resize, so a TV/kiosk
  // never auto-zooms to tasker clusters. Only active when fixedView is set.
  applyFixedView() {
    if (this.fixedViewValue !== "australia") return

    const frame = () => {
      const map = this.getMap()
      if (!map) {
        setTimeout(frame, 300)
        return
      }
      const fit = () => this.frameAustralia(map)
      if (map.loaded()) {
        fit()
      } else {
        map.on("load", fit)
      }
      // Re-frame on resize so the continent stays centred on any display size.
      this.resizeHandler = () => fit()
      window.addEventListener("resize", this.resizeHandler)
    }
    frame()
  }

  // Single source of truth for the full-continent framing, used by both the
  // initial fixed-view and the on-screen "home" button.
  frameAustralia(map) {
    map.fitBounds(this.constructor.AUSTRALIA_BOUNDS, { padding: 24, animate: false })
    // Nudge back half a zoom level so the continent sits a touch smaller
    // with breathing room all round, rather than edge-to-edge.
    map.setZoom(map.getZoom() - 0.5)
  }

  // --- on-screen zoom controls (TV-remote friendly) ---

  zoomIn() {
    const map = this.getMap()
    if (!map) return
    map.zoomTo(map.getZoom() + 0.5, { duration: 300 })
  }

  zoomOut() {
    const map = this.getMap()
    if (!map) return
    map.zoomTo(map.getZoom() - 0.5, { duration: 300 })
  }

  fitAustralia() {
    const map = this.getMap()
    if (!map) return
    this.frameAustralia(map)
  }

  // --- private ---

  addLayer() {
    const map = this.getMap()
    if (!map) {
      // Map not initialised yet -- retry shortly
      setTimeout(() => this.addLayer(), 300)
      return
    }

    const load = () => {
      fetch(this.urlValue, { credentials: 'same-origin' })
        .then(r => r.json())
        .then(geojson => {
          // If source already exists just update data; otherwise add source + layer
          const existing = map.getSource("live-places")
          if (existing) {
            existing.setData(geojson)
            return
          }

          map.addSource("live-places", { type: "geojson", data: geojson })
          map.addLayer({
            id:     "live-places-circles",
            type:   "circle",
            source: "live-places",
            paint: {
              "circle-radius":       6,
              "circle-color":        ["get", "color"],
              "circle-opacity":      0.9,
              "circle-stroke-width": 1.5,
              "circle-stroke-color": "#ffffff"
            }
          })

          // Hover popup
          const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10 })

          map.on("mouseenter", "live-places-circles", (e) => {
            map.getCanvas().style.cursor = "pointer"
            const props = e.features[0].properties
            popup.setLngLat(e.features[0].geometry.coordinates)
                 .setHTML(`<strong style="font-size:0.8rem;">${props.name}</strong><br><span style="font-size:0.75rem;color:#6b7280;text-transform:capitalize;">${props.status}</span>`)
                 .addTo(map)
          })

          map.on("mouseleave", "live-places-circles", () => {
            map.getCanvas().style.cursor = ""
            popup.remove()
          })
        })
        .catch(e => console.warn("[live-map-places] initial load failed", e))
    }

    // Mirror Mapkick's onMapLoad pattern: check loaded() first so we don't
    // miss the event if the map has already fully initialised
    if (map.loaded()) {
      load()
    } else {
      map.on("load", load)
    }
  }

  refreshLayer() {
    const map = this.getMap()
    if (!map) return

    fetch(this.urlValue, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(geojson => {
        const source = map.getSource("live-places")
        if (source) source.setData(geojson)
      })
      .catch(e => console.warn("[live-map-places] refresh failed", e))
  }

  // --- active-tasker layer (only when taskersUrlValue is set) ---
  // The taskers endpoint returns a PLAIN ARRAY in Mapkick format, not GeoJSON,
  // so we transform it client-side. Larger radius + sapphire stroke keeps these
  // visually distinct from the smaller, status-coloured store dots.

  addTaskersLayer() {
    const map = this.getMap()
    if (!map) {
      setTimeout(() => this.addTaskersLayer(), 300)
      return
    }

    const load = () => {
      fetch(this.taskersUrlValue, { credentials: 'same-origin' })
        .then(r => r.json())
        .then(points => {
          const geojson = this.taskersToGeoJson(points)

          const existing = map.getSource("live-taskers")
          if (existing) {
            existing.setData(geojson)
            return
          }

          map.addSource("live-taskers", { type: "geojson", data: geojson })
          map.addLayer({
            id:     "live-taskers-circles",
            type:   "circle",
            source: "live-taskers",
            paint: {
              "circle-radius":       8,
              "circle-color":        ["get", "color"],
              "circle-opacity":      0.95,
              "circle-stroke-width": 2,
              "circle-stroke-color": "#003d68" // sapphire -- "this is a tasker, not a store"
            }
          })

          const popup = new mapboxgl.Popup({ closeButton: false, closeOnClick: false, offset: 10 })

          map.on("mouseenter", "live-taskers-circles", (e) => {
            map.getCanvas().style.cursor = "pointer"
            const props = e.features[0].properties
            popup.setLngLat(e.features[0].geometry.coordinates)
                 .setHTML(`<strong style="font-size:0.8rem;">${props.label || ""}</strong><br><span style="font-size:0.75rem;color:#6b7280;">${props.tooltip || ""}</span>`)
                 .addTo(map)
          })

          map.on("mouseleave", "live-taskers-circles", () => {
            map.getCanvas().style.cursor = ""
            popup.remove()
          })
        })
        .catch(e => console.warn("[live-map-places] taskers load failed", e))
    }

    if (map.loaded()) {
      load()
    } else {
      map.on("load", load)
    }
  }

  refreshTaskersLayer() {
    const map = this.getMap()
    if (!map) return

    fetch(this.taskersUrlValue, { credentials: 'same-origin' })
      .then(r => r.json())
      .then(points => {
        const source = map.getSource("live-taskers")
        if (source) source.setData(this.taskersToGeoJson(points))
      })
      .catch(e => console.warn("[live-map-places] taskers refresh failed", e))
  }

  // Mapkick array -> GeoJSON FeatureCollection. Filters the empty-state sentinel
  // ("No Records Found") so no stray red Brisbane dot appears when no shifts are
  // in progress.
  taskersToGeoJson(points) {
    const features = (Array.isArray(points) ? points : [])
      .filter(p => {
        const name = p.name || p.label
        return name !== "No Records Found"
      })
      .filter(p => p.longitude != null && p.latitude != null)
      .map(p => ({
        type: "Feature",
        geometry: { type: "Point", coordinates: [p.longitude, p.latitude] },
        properties: { label: p.label || p.name || "", tooltip: p.tooltip || "", color: p.color || "#00FF00" }
      }))
    return { type: "FeatureCollection", features }
  }

  getMap() {
    // Mapkick stores map instances in window.Mapkick.maps keyed by element id
    const mapkick = window.Mapkick
    if (!mapkick) return null
    const instance = mapkick.maps[this.mapIdValue]
    return instance ? instance.map : null
  }
}
